import { useEffect, useMemo, useState } from 'react';
import { AssetTypes } from 'src/features/asset-details/types/asset';
import { ReportType } from 'src/features/reports';
import { SiteDropdownOption } from 'src/features/reports/config/pdfReportConfig';
import { useFetchAndSetAssets } from 'src/features/reports/hooks/useFetchAndSetAssets';
import { AssetService } from 'src/services/assets/asset.service';
import { SitesService } from 'src/services/sites/sites.service';
import { NewSite } from 'src/types/sites/sites';

type Props = {
  customerId: string | null;
  reportType: string | null;
};

export const usePDFReportData = ({ customerId, reportType }: Props) => {
  const [assetSiteIds, setAssetSiteIds] = useState<string[]>([]);
  const [isPDFDataLoading, setIsPDFDataLoading] = useState<boolean>(true);
  const assetType = AssetTypes.MBR_TRAIN;
  const { assets } = useFetchAndSetAssets({ customerId, assetType });
  const [customerSites, setCustomerSites] = useState<{
    loaded: boolean;
    value: SiteDropdownOption[] | undefined;
  }>({ loaded: false, value: [] });

  useEffect(() => {
    if (reportType !== ReportType.PDF && reportType !== ReportType.PDF_SITE_STATUS_REPORT) {
      setIsPDFDataLoading(false);
      return;
    }
    if (!customerId) {
      setIsPDFDataLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const assetData = await AssetService.getAll(customerId, undefined, {
          customerId,
          assetType: assetType,
        });
        const siteIds = assetData.map((item: any) => item.siteId);
        setAssetSiteIds(siteIds);

        const siteData = await SitesService.getAll({ customerId });
        const sites = siteData.map((site: NewSite) => ({
          label: site.siteName,
          value: site.id,
          location: site.location,
        }));
        setCustomerSites({ loaded: true, value: sites });
      } catch (err) {
        console.error(err);
        setCustomerSites({ loaded: true, value: undefined });
      } finally {
        setIsPDFDataLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, reportType]);

  const filteredCustomerSites = useMemo(
    () => customerSites.value?.filter((site) => assetSiteIds.includes(site.value)),
    [customerSites.value, assetSiteIds]
  );

  return {
    filteredCustomerSites,
    isPDFDataLoading,
    customerSites,
    assets,
  };
};
